<template>
  <v-container>
    <v-row>
      <v-col>
        <h3 class="mt-4">Data Sync to Public</h3>
        <p class="mt-3">Synchronize data from local system to public database.</p>
      </v-col>
    </v-row>

    <v-row class="mt-4">
      <v-col md="6">
        <v-card>
          <v-card-title>
            Sync Configuration
          </v-card-title>
          <v-card-text>
            <v-form>
              <v-select
                v-model="selectedJobType"
                :items="jobTypes"
                label="Select Job Type"
                outlined
                dense
              ></v-select>
              <template v-if="showTaxYearRange || showDropCollectionSwitch">
                <v-row v-if="showTaxYearRange">
                  <v-col>
                    <v-select
                      v-model="criteria.fromTaxYear"
                      :items="taxYearList"
                      label="From Tax Year"
                      outlined
                      dense
                    ></v-select>
                  </v-col>
                  <v-col>
                    <v-select
                      v-model="criteria.toTaxYear"
                      :items="taxYearList"
                    label="To Tax Year"
                    outlined
                    dense
                  ></v-select>
                  </v-col>
                </v-row>
                <BinaryToggle
                  v-if="showDropCollectionSwitch"
                  class="mt-2"
                  id="dataSyncDropCollectionToggle"
                  aria-label="Before syncing: keep or drop the public collection"
                  off-label="Keep collection"
                  on-label="Drop collection"
                  v-model="criteria.dropCollectionBeforeSync" />
              </template>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col md="6">
        <v-card>
          <v-card-title>
            Sync Status
          </v-card-title>
          <v-card-text>
            <div>
              <p class="mt-2">{{ jobSyncInfo }}</p>
              <p class="mt-2">{{ syncStatusMessage }}</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mt-4">
      <v-col>
        <v-btn
          color="primary"
          :disabled="syncInProgress || !selectedJobType"
          @click="startSync"
        >
          <v-icon left>mdi-sync</v-icon>
          Start Sync
        </v-btn>

        <!-- <v-btn
          color="secondary"
          class="ml-2"
          :disabled="!syncInProgress"
          @click="cancelSync"
        >
          <v-icon left>mdi-stop</v-icon>
          Cancel
        </v-btn>

        <v-btn
          color="info"
          class="ml-2"
          @click="viewLogs"
        >
          <v-icon left>mdi-file-document</v-icon>
          View Logs
        </v-btn> -->
      </v-col>
    </v-row>

    <v-row class="mt-4" v-if="syncLogs.length > 0">
      <v-col>
        <v-card>
          <v-card-title>
            Sync Logs
          </v-card-title>
          <v-card-text>
            <v-list dense>
              <v-list-item
                v-for="(log, index) in syncLogs"
                :key="index"
                :class="getLogClass(log.type)"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ log.status }}</v-list-item-title>
                  <v-list-item-subtitle v-if="log.errors.length > 0">
                    {{ log.errors }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';
import BinaryToggle from '../common/base/BinaryToggle.vue';
import { clients } from '../../util/clients';
import { DEFAULT_MIN_TAX_YEAR } from '../tax/common-utils/constants';

const { backendRest } = clients.direct;

const TAX_YEAR_RANGE_JOBS = [
  'bulkUpdateTaxInquiryToPublic',
  'bulkUpdateTaxPaymentToPublic',
  'bulkUpdateStatusCodesToPublic',
];

const DROP_COLLECTION_JOBS = [
  'bulkUpdateTaxInquiryToPublic',
  'bulkUpdateTaxFeeToPublic',
  'bulkUpdateTaxPaymentToPublic',
];

export default {
  name: 'DataSyncToPublic',
  components: { BinaryToggle },
  mounted() {
    this.fetchTaxYearList();
    this.getAllJobs().then((jobs) => {
      if (jobs.length > 0) {
        const activeJob = this.getActiveJob(jobs);
        if (activeJob) {
          this.syncInProgress = true;
          this.jobSyncInfo = `Active job found: ${activeJob.jobId}`;
          this.poolingJobStatus(activeJob.jobId);
        } else {
          this.jobSyncInfo = 'No active jobs found';
        }
      } else {
        this.jobSyncInfo = 'No jobs available';
      }
    });
  },
  data() {
    return {
      selectedJobType: null,
      syncInProgress: false,
      syncStatusMessage: '',
      jobSyncInfo: '',
      syncLogs: [],
      taxYearList: [],
      jobTypes: [
        { text: 'Taxroll', value: 'bulkUpdateTaxInquiryToPublic' },
        { text: 'Payments', value: 'bulkUpdateTaxPaymentToPublic' },
        { text: 'Fees', value: 'bulkUpdateTaxFeeToPublic' },
        { text: 'Memo', value: 'bulkUpdateTaxMemoToPublic' },
        { text: 'Status Codes', value: 'bulkUpdateStatusCodesToPublic' },
        { text: 'Additions', value: 'bulkUpdateAdditionsToPublic' },
        // { text: 'All Data', value: 'all' },
      ],
      criteria: {
        fromTaxYear: null,
        toTaxYear: null,
        dropCollectionBeforeSync: true,
      },
    };
  },
  computed: {
    showTaxYearRange() {
      return TAX_YEAR_RANGE_JOBS.includes(this.selectedJobType);
    },
    // The public tables have no unique key and no upsert, so a sync that does
    // not drop first appends a second copy of every row. Any job whose sync
    // honours the flag must expose the switch.
    showDropCollectionSwitch() {
      return DROP_COLLECTION_JOBS.includes(this.selectedJobType);
    },
  },
  methods: {
    ...mapActions('tax/taxCommon', ['loadTaxYearList']),
    async fetchTaxYearList() {
      try {
        const taxYears = await this.loadTaxYearList({
          allowAll: false,
          dontCommit: true,
        });
        if (taxYears && taxYears.length > 0) {
          this.taxYearList = taxYears;
          const minTaxYear = this.taxYearList[this.taxYearList.length - 1];
          this.criteria.fromTaxYear = minTaxYear > DEFAULT_MIN_TAX_YEAR
            ? minTaxYear : DEFAULT_MIN_TAX_YEAR;
          [this.criteria.toTaxYear] = this.taxYearList;
        }
      } catch (error) {
        console.error('Error loading tax year list:', error);
      }
    },
    async startSync() {
      if (window.confirm(`Are you sure you want to start the ${this.selectedJobType} sync?`)) {
        this.syncInProgress = true;
        try {
          const criteria = { ...this.criteria };
          const response = await backendRest.post('/jobs/bulk-update-to-public-by-type', {
            jobName: this.selectedJobType,
            params: {
              criteria,
            },
          });
          const { data } = response;
          if (data.success) {
            const { jobId } = data;
            this.jobSyncInfo = `Job started successfully with ID: ${jobId}`;
            this.poolingJobStatus(jobId);
          } else {
            throw new Error(data.message || 'Failed to start job');
          }
        } catch (error) {
          // do nothing
          console.error('Error starting sync job:', error);
        } finally {
          // do nothing
        }
      } else {
        // do nothing
      }
    },

    getLogClass(type) {
      switch (type) {
        case 'error': return 'error--text';
        case 'warning': return 'warning--text';
        case 'success': return 'success--text';
        default: return '';
      }
    },
    async getAllJobs() {
      let jobs = [];
      try {
        const response = await backendRest.get('/jobs/bulk-update-to-public-status/getAllJobs');
        if (response.data.success && (response.data.jobs || []).length > 0) {
          jobs = response.data.jobs;
          // You can set initial values based on the response if needed
          this.syncLogs = response.data.jobs.map((job) => ({
            type: 'info',
            status: `Job ID: ${job.jobId}, ${job.jobName}, ${job.status}, ${job.recordsProcessed} records processed, started at ${job.startTime}`,
            errors: job.errors || [],
          }));
        }
      } catch (error) {
        console.error('Error loading all jobs:', error);
      }
      return jobs;
    },
    async getJobStatusById(jobId) {
      try {
        const response = await backendRest.get(`/jobs/bulk-update-to-public-status/getJobById/${jobId}`);
        if (response.data.success) {
          return response.data.job;
        }
        return null;
      } catch (error) {
        console.error('Error fetching job status:', error);
        return null;
      }
    },
    getActiveJob(jobs) {
      return (jobs || []).find((log) => log.status.includes('in-progress'));
    },
    async poolingJobStatus(jobId) {
      try {
        const job = await this.getJobStatusById(jobId);
        if (job) {
          this.syncStatusMessage = `Job ID: ${job.jobId}, Job Name: ${job.jobName}, Status: ${job.status}, Records Processed: ${job.recordsProcessed}`;
          if (job.status === 'completed') {
            this.syncInProgress = false;
          } else if (job.status === 'error') {
            this.syncInProgress = false;
          } else if (job.status === 'in-progress') {
            this.syncInProgress = true;
            setTimeout(() => {
              this.poolingJobStatus(jobId);
            }, 5000); // Poll every 5 seconds
          }
        } else {
          this.syncStatusMessage = 'Failed to retrieve job status';
        }
      } catch (error) {
        console.error('Error pooling job status:', error);
      }
    },
  },
};
</script>

<style scoped>
.error--text {
  color: #f44336 !important;
}

.warning--text {
  color: #ff9800 !important;
}

.success--text {
  color: #4caf50 !important;
}
</style>
